import { logger } from '@/lib/logger';

import type { SubmitLane } from './executor/types';

export type CheckoutStep = 'select' | 'pending' | 'submitted' | 'submitFailed';
export type CheckoutState = { step: CheckoutStep };

export const INITIAL_CHECKOUT_STATE: CheckoutState = { step: 'select' };

export type CheckoutEvent =
  | { type: 'submitStarted'; lane: SubmitLane }
  | { type: 'submitSucceeded'; lane: SubmitLane }
  | { type: 'submitFailed'; lane: SubmitLane }
  | { type: 'submitAborted'; lane: SubmitLane }
  | { type: 'reset' };

/**
 * Owns all checkout policy: the executor reports lifecycle facts
 * unconditionally and this reducer decides what they mean per lane. Advanced
 * submissions skip the pending screen entirely: success jumps `select` to
 * `submitted`, while every other advanced lifecycle event is absorbed silently so
 * failures leave the form interactive. Dismissing the status screen dispatches
 * `reset`, returning to `select` while the in-flight transaction keeps
 * processing in the background; completion events arriving after such a
 * dismissal are absorbed silently. Any other invalid transition is ignored and
 * logged, flagging the dispatching caller as buggy.
 */
export function reduceCheckout(state: CheckoutState, event: CheckoutEvent): CheckoutState {
  if (event.type === 'reset') {
    return INITIAL_CHECKOUT_STATE;
  }

  if (event.lane === 'advanced') {
    if (event.type === 'submitSucceeded' && state.step === 'select') {
      return { step: 'submitted' };
    }
    return state;
  }

  if (
    event.type === 'submitStarted' &&
    (state.step === 'select' || state.step === 'submitFailed')
  ) {
    return { step: 'pending' };
  }

  if (state.step === 'pending') {
    if (event.type === 'submitSucceeded') {
      return { step: 'submitted' };
    }

    if (event.type === 'submitFailed') {
      return { step: 'submitFailed' };
    }

    if (event.type === 'submitAborted') {
      return INITIAL_CHECKOUT_STATE;
    }
  }

  // Late completion after a mid-flight dismissal: legitimate, not a caller bug.
  if (state.step === 'select') {
    return state;
  }

  logger.warn('checkout: ignored transition', { step: state.step, event });
  return state;
}
